(() => {
  "use strict";

  /*
   * Mortgage Cost Analysis Intake Controller
   * Purpose:
   * - Controls the existing .cv-screen Liquid UI
   * - Saves paid answers using the exact field names CreditViewEngine expects
   * - Runs CreditViewEngine.run(state)
   * - Saves modern + legacy localStorage keys
   *
   * Replace asset:
   *   mortgage-cost-analysis-intake.js
   *
   * Do NOT change:
   *   credit-view-engine.js
   *   mortgage-cost-analysis Liquid
   */

  const SESSION_KEY = "credit_optimize_session";
  const LEGACY_ANALYSIS_KEY = "mortgage_cost_analysis_data";
  const LEGACY_REPORT_KEY = "credit_view_data";
  const RESULTS_URL = "/pages/mortgage-cost-analysis-results";

  const app = document.getElementById("creditViewApp");
  if (!app) return;

  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";

  const screens = Array.from(app.querySelectorAll("[data-screen]"));

  const order = [
    "intro",
    "purpose",
    "timeline",
    "open_cards",
    "cards_with_balances",
    "cards_over_50_count",
    "over_50_details",
    "smallest_balances",
    "mortgage_late",
    "other_lates",
    "worst_late",
    "collections_count",
    "collection_recognized",
    "collection_paid_status",
    "collection_recency",
    "credit_age",
    "credit_mix",
    "loan_range",
    "complete"
  ];

  let current = "intro";

  let state = {
    purpose: null,
    timeline: null,

    open_cards: null,
    cards_with_balances: null,
    cards_over_50_count: null,
    over_50_cards: [],
    smallest_reporting_balances: [],

    mortgage_late: null,
    other_lates: null,
    worst_late: null,

    collections_count: null,
    collection_recognized: null,
    collection_paid_status: null,
    collection_recency: null,

    credit_age: null,
    credit_mix: [],
    loan_range: null
  };

  function loadSession() {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function saveSession(session) {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (e) {}
  }

  function screenByName(name) {
    return screens.find(screen => screen.getAttribute("data-screen") === name);
  }

  function currentScreen() {
    return screenByName(current);
  }

  function hasValue(value) {
    return value !== null && value !== undefined && value !== "";
  }

  function moneyToNumber(value) {
    return Number(String(value || "").replace(/[^0-9.]/g, "") || 0);
  }

  function formatMoneyInput(input) {
    const raw = String(input.value || "").replace(/[^0-9]/g, "");
    if (!raw) {
      input.value = "";
      return;
    }
    input.value = "$" + Number(raw).toLocaleString("en-US");
  }

  function selectValue(screen) {
    const selected = screen.querySelector(".cv-option.is-selected");
    return selected ? selected.getAttribute("data-value") : null;
  }

  function setError(message) {
    const screen = currentScreen();
    if (!screen) return;

    const box = screen.querySelector(".cv-error");
    if (!box) return;

    if (message) {
      box.textContent = message;
      box.classList.add("is-visible");
    } else {
      box.textContent = "";
      box.classList.remove("is-visible");
    }
  }

  function fail(message) {
    setError(message);
    return false;
  }

  function getOver50Count() {
    if (state.cards_over_50_count === "unknown") return 0;
    return Number(state.cards_over_50_count || 0);
  }

  function targetReportingCards() {
    const open = Number(state.open_cards || 0);
    if (open <= 1) return open;

    // Must match engine targetReportingCards().
    return Math.max(0, Math.ceil(open * 0.5) - 1);
  }

  function cardsToZeroCount() {
    const reporting = Number(state.cards_with_balances || 0);
    return Math.max(0, reporting - targetReportingCards());
  }

  function needsSmallestBalances() {
    return Number(state.open_cards || 0) > 0 && cardsToZeroCount() > 0;
  }

  function collectionsExist() {
    return ["1", "2", "3_plus"].includes(String(state.collections_count));
  }

  function otherLatesExist() {
    return ["within_12", "12_24", "older_24"].includes(String(state.other_lates));
  }

  function shouldSkip(name) {
    if (name === "intro" || name === "complete") return false;

    if (state.open_cards === "0" && [
      "cards_with_balances",
      "cards_over_50_count",
      "over_50_details",
      "smallest_balances"
    ].includes(name)) {
      return true;
    }

    if (name === "over_50_details" && getOver50Count() <= 0) return true;
    if (name === "smallest_balances" && !needsSmallestBalances()) return true;

    if (name === "worst_late" && !otherLatesExist()) return true;

    if ([
      "collection_recognized",
      "collection_paid_status",
      "collection_recency"
    ].includes(name) && !collectionsExist()) {
      return true;
    }

    return false;
  }

  function visibleOrder() {
    return order.filter(name => !shouldSkip(name));
  }

  function nextScreenName() {
    const visible = visibleOrder();
    const idx = visible.indexOf(current);
    return visible[Math.min(visible.length - 1, idx + 1)];
  }

  function previousScreenName() {
    const visible = visibleOrder();
    const idx = visible.indexOf(current);
    return visible[Math.max(0, idx - 1)];
  }

  function savePartial() {
    const session = loadSession();

    session.freeCheck = session.freeCheck || {};
    session.mortgageCostAnalysis = {
      ...(session.mortgageCostAnalysis || {}),
      ...state,
      updatedAt: new Date().toISOString()
    };

    session.mergedAnswers = {
      ...session.freeCheck,
      ...state
    };

    session.updatedAt = new Date().toISOString();

    saveSession(session);

    try {
      localStorage.setItem(LEGACY_ANALYSIS_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function saveCurrent() {
    const screen = currentScreen();
    if (!screen) return true;

    const key = screen.getAttribute("data-key");
    if (!key) return true;

    setError("");

    if (key === "cards_with_balances") {
      const input = screen.querySelector('[data-input="cards_with_balances"]');
      const val = moneyToNumber(input ? input.value : "");

      if (isNaN(val) || val < 0) {
        return fail("Enter the number of cards reporting balances.");
      }

      if (Number(state.open_cards || 0) > 0 && val > Number(state.open_cards)) {
        return fail("Cards reporting balances cannot be higher than open cards.");
      }

      state.cards_with_balances = val;
      savePartial();
      return true;
    }

    if (key === "cards_over_50_count") {
      const selected = selectValue(screen);
      if (!selected) return fail("Select an answer to continue.");

      state.cards_over_50_count = selected;

      if (getOver50Count() <= 0) {
        state.over_50_cards = [];
      }

      savePartial();
      return true;
    }

    if (key === "over_50_details") {
      const rows = Array.from(screen.querySelectorAll("[data-over50-row]"));
      const cards = [];

      for (let i = 0; i < rows.length; i++) {
        const balInput = rows[i].querySelector("[data-over50-balance]");
        const limInput = rows[i].querySelector("[data-over50-limit]");

        const balance = moneyToNumber(balInput ? balInput.value : "");
        const limit = moneyToNumber(limInput ? limInput.value : "");

        if (!balance || !limit) {
          return fail("Enter both balance and limit for each over-50% card.");
        }

        if (balance > limit * 1.25) {
          return fail("One balance looks much higher than its limit. Please check the number.");
        }

        cards.push({ balance, limit });
      }

      state.over_50_cards = cards;
      savePartial();
      return true;
    }

    if (key === "smallest_balances") {
      const inputs = Array.from(screen.querySelectorAll("[data-smallest-balance]"));
      const balances = [];

      for (let i = 0; i < inputs.length; i++) {
        const amount = moneyToNumber(inputs[i].value);

        if (!amount) {
          return fail("Enter each smallest reporting balance.");
        }

        balances.push(amount);
      }

      state.smallest_reporting_balances = balances;
      savePartial();
      return true;
    }

    if (screen.getAttribute("data-multi") === "true") {
      const selected = Array.from(screen.querySelectorAll(".cv-option.is-selected"))
        .map(option => option.getAttribute("data-value"))
        .filter(Boolean);

      if (!selected.length) {
        return fail("Select at least one answer to continue.");
      }

      state[key] = selected;
      savePartial();
      return true;
    }

    const selected = selectValue(screen);

    if (!selected) {
      return fail("Select an answer to continue.");
    }

    state[key] = selected;
    savePartial();
    return true;
  }

  function buildOver50Fields() {
    const count = getOver50Count();
    const list = app.querySelector("[data-over50-list]");
    if (!list) return;

    list.innerHTML = "";

    const exactRows = Math.min(count, 4);

    for (let i = 1; i <= exactRows; i++) {
      const row = document.createElement("div");
      row.className = "cv-row-card";
      row.setAttribute("data-over50-row", "");
      row.innerHTML =
        '<div class="cv-row-title">Card over 50% — ' + i + '</div>' +
        '<div class="cv-row-fields">' +
          '<div class="cv-field">' +
            '<label class="cv-label">Balance</label>' +
            '<input class="cv-input" data-money data-over50-balance inputmode="numeric" autocomplete="off" placeholder="$">' +
          '</div>' +
          '<div class="cv-field">' +
            '<label class="cv-label">Limit</label>' +
            '<input class="cv-input" data-money data-over50-limit inputmode="numeric" autocomplete="off" placeholder="$">' +
          '</div>' +
        '</div>';

      list.appendChild(row);
    }

    if (count >= 5) {
      const row = document.createElement("div");
      row.className = "cv-row-card";
      row.setAttribute("data-over50-row", "");
      row.innerHTML =
        '<div class="cv-row-title">Remaining over-50% cards — combined</div>' +
        '<div class="cv-row-fields">' +
          '<div class="cv-field">' +
            '<label class="cv-label">Combined balance</label>' +
            '<input class="cv-input" data-money data-over50-balance inputmode="numeric" autocomplete="off" placeholder="$">' +
          '</div>' +
          '<div class="cv-field">' +
            '<label class="cv-label">Combined limit</label>' +
            '<input class="cv-input" data-money data-over50-limit inputmode="numeric" autocomplete="off" placeholder="$">' +
          '</div>' +
        '</div>';

      list.appendChild(row);
    }
  }

  function buildSmallestFields() {
    const count = cardsToZeroCount();
    const list = app.querySelector("[data-smallest-list]");
    const title = app.querySelector("[data-smallest-title]");

    if (title) {
      if (count === 1) title.textContent = "Enter the smallest reporting balance.";
      else if (count === 2) title.textContent = "Enter the two smallest reporting balances.";
      else if (count === 3) title.textContent = "Enter the three smallest reporting balances.";
      else title.textContent = "Enter the smallest reporting balances.";
    }

    if (!list) return;

    list.innerHTML = "";

    for (let i = 1; i <= count; i++) {
      const row = document.createElement("div");
      row.className = "cv-row-card";
      row.innerHTML =
        '<div class="cv-row-title">' +
          (count === 1 ? "Smallest reporting balance" : "Smallest reporting balance — " + i) +
        '</div>' +
        '<div class="cv-field">' +
          '<label class="cv-label">Balance</label>' +
          '<input class="cv-input" data-money data-smallest-balance inputmode="numeric" autocomplete="off" placeholder="$">' +
        '</div>';

      list.appendChild(row);
    }
  }

  function stepScreens() {
    return visibleOrder().filter(name => name !== "intro" && name !== "complete");
  }

  function drawProgress() {
    const visible = stepScreens();
    const total = visible.length || 1;
    const index = visible.indexOf(current);

    app.querySelectorAll("[data-step-label]").forEach(label => {
      const screen = label.closest("[data-screen]");
      const name = screen ? screen.getAttribute("data-screen") : "";
      const i = visible.indexOf(name);
      label.textContent = i > -1 ? (i + 1) + "/" + total : "";
    });

    app.querySelectorAll(".cv-progress").forEach(progress => {
      progress.innerHTML = "";

      for (let i = 0; i < 5; i++) {
        const segment = document.createElement("span");
        const pct = current === "complete" ? 1 : Math.max(0.05, (index + 1) / total);

        if (i < Math.ceil(pct * 5)) {
          segment.className = "is-on";
        }

        progress.appendChild(segment);
      }
    });
  }

  function show(name) {
    const visible = visibleOrder();

    if (!visible.includes(name)) {
      name = visible[0] || "intro";
    }

    const target = screenByName(name);
    if (!target) return;

    current = name;

    if (current === "over_50_details") buildOver50Fields();
    if (current === "smallest_balances") buildSmallestFields();

    screens.forEach(screen => screen.classList.remove("is-active"));
    target.classList.add("is-active");

    const scroller = target.querySelector(".cv-scroll");
    if (scroller) scroller.scrollTop = 0;

    drawProgress();
  }

  function clearOldReportKeys() {
    try {
      localStorage.removeItem(LEGACY_REPORT_KEY);
      localStorage.removeItem("credit_view_intake");
      localStorage.removeItem("credit_view_answers");
    } catch (e) {}
  }

  function buildReport() {
    if (window.CreditViewEngine && typeof window.CreditViewEngine.run === "function") {
      return window.CreditViewEngine.run(state);
    }

    return {
      status: "engine_missing",
      message: "CreditViewEngine.run was not available.",
      answers: state,
      generatedAt: new Date().toISOString()
    };
  }

  function finish() {
    if (!saveCurrent()) return;

    const report = buildReport();

    const session = loadSession();

    session.freeCheck = session.freeCheck || {};
    session.mortgageCostAnalysis = {
      ...state,
      updatedAt: new Date().toISOString()
    };
    session.mergedAnswers = {
      ...state
    };
    session.report = report;
    session.updatedAt = new Date().toISOString();

    saveSession(session);

    try {
      localStorage.setItem(LEGACY_ANALYSIS_KEY, JSON.stringify(state));
      localStorage.setItem(LEGACY_REPORT_KEY, JSON.stringify(report));

      // Temporary compatibility for any old results page readers.
      localStorage.setItem("credit_view_intake", JSON.stringify(report));
      localStorage.setItem("credit_view_answers", JSON.stringify(report));
    } catch (e) {}

    window.location.href = RESULTS_URL;
  }


  function resetPaidSessionForNewRun() {
    const session = loadSession();

    session.freeCheck = session.freeCheck || {};
    session.mortgageCostAnalysis = {};
    session.mergedAnswers = {};
    session.report = null;
    session.updatedAt = new Date().toISOString();

    saveSession(session);

    try {
      localStorage.removeItem(LEGACY_REPORT_KEY);
      localStorage.removeItem(LEGACY_ANALYSIS_KEY);
      localStorage.removeItem("credit_view_intake");
      localStorage.removeItem("credit_view_answers");
    } catch (e) {}

    state = {
      purpose: null,
      timeline: null,

      open_cards: null,
      cards_with_balances: null,
      cards_over_50_count: null,
      over_50_cards: [],
      smallest_reporting_balances: [],

      mortgage_late: null,
      other_lates: null,
      worst_late: null,

      collections_count: null,
      collection_recognized: null,
      collection_paid_status: null,
      collection_recency: null,

      credit_age: null,
      credit_mix: [],
        loan_range: null
    };

    screens.forEach(screen => {
      screen.querySelectorAll(".cv-option").forEach(btn => btn.classList.remove("is-selected"));
      screen.querySelectorAll("input").forEach(input => {
        input.value = "";
      });
    });

    const banner = document.getElementById("cvImportBanner");
    if (banner) banner.style.display = "none";
  }

  function hydrateFromExistingPaidSession() {
    const session = loadSession();

    if (session && session.mortgageCostAnalysis) {
      state = {
        ...state,
        ...session.mortgageCostAnalysis
      };
    }

    const banner = document.getElementById("cvImportBanner");
    if (banner) banner.style.display = "none";
  }

  function applyStoredSelections() {
    screens.forEach(screen => {
      const key = screen.getAttribute("data-key");
      if (!key) return;

      const value = state[key];

      if (screen.getAttribute("data-multi") === "true") {
        const values = Array.isArray(value) ? value : [];

        values.forEach(v => {
          const btn = screen.querySelector('.cv-option[data-value="' + String(v).replace(/"/g, '\\"') + '"]');
          if (btn) btn.classList.add("is-selected");
        });

        return;
      }

      if (hasValue(value)) {
        const btn = screen.querySelector('.cv-option[data-value="' + String(value).replace(/"/g, '\\"') + '"]');
        if (btn) btn.classList.add("is-selected");
      }

      const input = screen.querySelector("[data-input]");
      if (input) {
        const inputKey = input.getAttribute("data-input");
        if (hasValue(state[inputKey])) input.value = state[inputKey];
      }
    });
  }

  function bindEvents() {
    app.addEventListener("click", event => {
      const option = event.target.closest(".cv-option");

      if (option && app.contains(option)) {
        const screen = option.closest("[data-screen]");
        const key = screen ? screen.getAttribute("data-key") : null;
        const isMulti = screen && screen.getAttribute("data-multi") === "true";

        if (!screen || !key) return;

        if (isMulti) {
          if (option.getAttribute("data-exclusive") === "true") {
            screen.querySelectorAll(".cv-option").forEach(btn => btn.classList.remove("is-selected"));
            option.classList.add("is-selected");
          } else {
            const exclusive = screen.querySelector('[data-exclusive="true"]');
            if (exclusive) exclusive.classList.remove("is-selected");
            option.classList.toggle("is-selected");
          }

          state[key] = Array.from(screen.querySelectorAll(".cv-option.is-selected"))
            .map(btn => btn.getAttribute("data-value"))
            .filter(Boolean);
        } else {
          screen.querySelectorAll(".cv-option").forEach(btn => btn.classList.remove("is-selected"));
          option.classList.add("is-selected");
          state[key] = option.getAttribute("data-value");
        }

        setError("");
        savePartial();
        return;
      }

      if (event.target.closest("[data-next]")) {
        if (!saveCurrent()) return;
        show(nextScreenName());
        return;
      }

      if (event.target.closest("[data-back]")) {
        show(previousScreenName());
        return;
      }

      if (event.target.closest("[data-finish]")) {
        finish();
      }
    });

    app.addEventListener("input", event => {
      const input = event.target.closest("input");
      if (!input) return;

      if (input.hasAttribute("data-money")) {
        formatMoneyInput(input);
      }

      const named = input.getAttribute("data-input");
      if (named) {
        state[named] = moneyToNumber(input.value);
        savePartial();
      }

      setError("");
    });

    app.addEventListener("keydown", event => {
      if (event.key !== "Enter") return;

      const input = event.target.closest("input");
      if (!input) return;

      const screen = currentScreen();
      if (!screen) return;

      const inputs = Array.from(screen.querySelectorAll("input"));
      const idx = inputs.indexOf(input);

      event.preventDefault();

      if (idx > -1 && idx < inputs.length - 1) {
        inputs[idx + 1].focus();
      } else {
        const next = screen.querySelector("[data-next]");
        if (next) next.click();
      }
    });
  }

  clearOldReportKeys();
  resetPaidSessionForNewRun();
  bindEvents();
  show("intro");
})();
